<template>
	<form class="flex flex-col h-fit sm:w-[600px] md:w-[745px] md:max-w-[745px] bg-neutral">
		<!-- Prevent implicit submission of the form -->
		<button type="submit" disabled style="display: none" aria-hidden="true"></button>

		<div v-if="breadcrumbs" class="flex flex-col">
			<div class="flex w-full space-x-3">
				<img src="@/assets/images/icon/arrow-left.svg" class="cursor-pointer" @click="cancel"/>
				<span v-html="breadcrumbs"/>
			</div>
			<span class="bg-[#B2BBC9] h-[1px] mt-4"/>
		</div>

		<div class="flex flex-col" :class="[{'mt-4': props.isSubsection}]">
			<InputText class="flex-1" :label="`Resource ${props.isSubsection ? 'Subsection' : 'Section'} Name`" type="text" input-height="h-11" font-weight="font-normal" v-model="section_name" error-field="name" :errors="error" />
		</div>

		<div v-if="resource_id">
			<SubmitButton class="ml-auto" :label="props.isSubsection ? 'Add Resource' : 'Add Subsection'" @click.prevent="addClicked" size="small" />

			<PortalDataTable
				ref="table"
				:server-mode="false"
				:endpoints="endpoints"
				:row-data="rows"
			>
				<template #name="data">
					<span class="clickable-text" @click="onRowClicked(data.value)">{{ data.value.name }}</span>
				</template>
			</PortalDataTable>
	</div>

		<div class="flex w-full mt-6">
			<div class="flex-grow">
				<SubmitButton v-if="resource_id" label="Delete" :type="ButtonType.Warning" :size="ButtonSize.Small"
											:disabled="isSaving || isDeleting" :loading="isDeleting" @click="onDeleteClicked" />
			</div>

			<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isSaving || isDeleting" class="ml-auto mr-3" @click="cancel" />
			<SubmitButton label="Save" :size="ButtonSize.Small" :loading="isSaving" @click="saveClicked" :disabled="disableSaveButton" />
		</div>
	</form>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDiscardChangesModal"
			icon="alert"
			title="Discard Changes"
			message="Are you sure? Any unsaved changes will be lost."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDiscardChangesModal = false }, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { emit('dismiss') }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDeleteModal"
			icon="alert"
			:title="props.isSubsection ? 'Delete Subsection' : 'Delete Section'"
			:message="`Are you sure?<br><br><b>Deleting this ${props.isSubsection ? 'subsection' : 'section'} will also delete all ${props.isSubsection ? 'resources' : 'subsections and resources'} associated with it</b>.<br><br>This cannot be undone.`"
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDeleteModal = false }, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Delete', () => { deleteSection() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>
</template>

<script setup lang="js">
import InputText from "@/components/Forms/InputText.vue"
import { computed, onMounted, ref } from 'vue'
import SubmitButton from "@/components/Forms/Buttons/SubmitButton.vue"
import { ButtonSize, ButtonType } from "@/components/Forms/Buttons/SubmitButtonConstants.js"
import { resourcesApi } from '@/api/Organizations/Resources/ResourcesApi.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const props = defineProps({
	show: { type: Boolean, default: false },
	resourceSectionToEdit: { type: Object, default: null },
	isSubsection: { type: Boolean, default: false },
})

const isSaving = ref(false)
const isDeleting = ref(false)
const error = ref(null)
const resource_id = ref(null)
const breadcrumbs = ref(null)
const section_name = ref(null)
const subsections = ref([])
const resources = ref([])
const showDiscardChangesModal = ref(false)
const showDeleteModal = ref(false)
const disableSaveButton = computed(() => {
	return isSaving.value || isDeleting.value || !section_name.value
})
const toaster = new Toaster()

const columns = computed(() => {
	return [
		{ field: "name", title: props.isSubsection ? "Resources" : "Subsections", sort: true },
	]
})

const endpoints = [
	new DataTableEndpointDefinition(
		{
			initialSortColumn: 'name',
			enableSearch: false,
			columns: columns,
		}
	)
]

const rows = computed(() => {
	return props.isSubsection ? resources.value : subsections.value
})

const emit = defineEmits(["dismiss", "subsectionClicked", "resourceClicked", 'addSubsectionClicked', 'addResourceClicked', 'subsectionDeleted', 'sectionDeleted'])

onMounted(() => {
	handleResourceSection(props.resourceSectionToEdit)
})

function onRowClicked(data) {
	if (props.isSubsection) {
		emit('resourceClicked', data)
	}
	else {
		emit('subsectionClicked', data)
	}
}

function handleResourceSection(resourceSection) {
	if (resourceSection) {
		resource_id.value = resourceSection.id
		parent.value = resourceSection.parent
		breadcrumbs.value = resourceSection.breadcrumbs
		section_name.value = resourceSection.name

		if (props.isSubsection) {
			resources.value = resourceSection.resources
		}
		else {
			subsections.value = resourceSection.subcategories
		}
	}
}

function cancel() {
	if (changesMade()) {
		showDiscardChangesModal.value = true
	} else {
		emit("dismiss")
	}
}

function changesMade() {
	let oldName = props.resourceSectionToEdit.name

	return section_name.value !== null && section_name.value !== oldName
}

function addClicked() {
	if (props.isSubsection) {
		emit('addResourceClicked')
	}
	else {
		emit('addSubsectionClicked')
	}
}

function saveClicked() {
	if (!changesMade()) {
		cancel()
		return
	}

	error.value = null
	isSaving.value = true

	const successCallback = (updatedResource) =>
	{
		isSaving.value = false
		emit('dismiss', updatedResource['data'])
	}

	const errorCallback = (e) =>
	{
		isSaving.value = false
		error.value = e.showErrorBanner()
	}

	if (resource_id.value == null) {
		if (props.isSubsection) {
			resourcesApi.createResourceSubcategory({
				'resource_subcategory': {
					'category_id': props.resourceSectionToEdit.category_id,
					'name': section_name.value,
				}
			}, successCallback, errorCallback)
		}
		else {
			resourcesApi.createResourceCategory({
				'resource_category': {
					'name': section_name.value,
				}
			}, successCallback, errorCallback)
		}
	}
	else {
		if (props.isSubsection) {
			resourcesApi.updateResourceSubcategory({
				'resource_subcategory': {
					'id': resource_id.value,
					'name': section_name.value,
				}
			}, successCallback, errorCallback)
		}
		else {
			resourcesApi.updateResourceCategory({
				'resource_category': {
					'id': resource_id.value,
					'name': section_name.value,
				}
			}, successCallback, errorCallback)
		}
	}
}

function onDeleteClicked() {
	showDeleteModal.value = true
}

function deleteSection() {
	error.value = null
	isDeleting.value = true

	if (props.isSubsection) {
		resourcesApi.deleteResourceSubcategory(props.resourceSectionToEdit.id,
			(success) => {
				error.value = null
				isDeleting.value = false

				if (success) {
					toaster.success('Subsection successfully deleted!')
					emit("subsectionDeleted", props.resourceSectionToEdit)
				}	else {
					toaster.error('Subsection failed to delete. Please try again.')
				}
			})
	}
	else {
		resourcesApi.deleteResourceCategory(props.resourceSectionToEdit.id,
			(success) => {
				error.value = null
				isDeleting.value = false

				if (success) {
					toaster.success('Section successfully deleted!')
					emit("sectionDeleted", props.resourceSectionToEdit)
				}	else {
					toaster.error('Section failed to delete. Please try again.')
				}
			})
	}
}
</script>